import React from 'react'
import styled, { css } from 'styled-components'
import { Tooltip } from '@streamr/streamr-layout'
import { SEMIBOLD, TABLET } from '@streamr/streamr-layout/styled'
import Display from '~shared/Display'
import useNetworkStats from '~hooks/useNetworkStats'

const Label = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    letter-spacing: 0.1rem;
    text-transform: uppercase;
    transition: 300ms color ease-in-out;
    color: inherit;

    strong {
        font-weight: ${SEMIBOLD};
        margin-right: 2px;
    }

    @media (min-width: ${TABLET}px) {
        font-size: 12px;

        strong {
            margin-right: 6px;
        }
    }
`

const UnstyledStat = ({ name, children, ...props }) => (
    <div {...props}>
        <Tooltip value={name} placement={Tooltip.BOTTOM}>
            <Label>
                {children}
            </Label>
        </Tooltip>
    </div>
)

const Stat = styled(UnstyledStat)`
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    ${Tooltip} {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }
`

const NodeIcon = () => (
    <svg width="8" height="9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="4" cy="4.5" r="3.375" stroke="#656E9B" strokeWidth="1.25" />
        <circle cx="4" cy="4.5" r="1.6" fill="#656E9B" />
    </svg>
)

const Root = styled.div`
    color: rgba(12, 0, 154, 0.1);
    transition: 200ms color;

    ${({ ready }) => !!ready && css`
        color: rgb(12, 0, 154);
    `}
`

const UnstyledNetworkStats = ({ className, data: { metadata = new WeakMap() } = {}, point }) => {
    const { topologySize } = metadata.get(point) || {}

    const { messagesPerSecond, numberOfNodes, ready } = useNetworkStats()

    const numNodes = point ? topologySize : numberOfNodes

    return (
        <Root className={className} ready={ready}>
            <Stat name="Network size">
                <strong>
                    {numNodes || '-'}
                </strong>
                <Display mobile="none" tablet="block">
                    {numNodes === 1 ? 'node' : 'nodes'}
                </Display>
                <Display mobile="block" tablet="none">
                    <NodeIcon />
                </Display>
            </Stat>
            <Stat name="Message traffic">
                <strong>{messagesPerSecond || '-'}</strong>
                Mps
            </Stat>
        </Root>
    )
}

const NetworkStats = styled(UnstyledNetworkStats)`
    cursor: default;
    display: flex;
    z-index: 3;

    ${Stat} {
        height: 32px;
        flex: 1;
    }

    ${Stat}:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    }

    ${Stat}:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }

    @media (min-width: ${TABLET}px) {
        width: fit-content;

        ${Stat} {
            width: 136px;
        }

        ${Stat} + ${Stat} {
            margin-left: 1px;
        }
    }
`

export default NetworkStats
